// Установка приложения (PWA) прямо из интерфейса, без меню браузера.
//
// В Chromium (Android Chrome, desktop Chrome/Edge, Samsung Internet) браузер
// шлет beforeinstallprompt ВМЕСТО собственной плашки установки, если событие
// отменить. Его надо перехватить как можно раньше (см. src/index.js) и
// придержать: показать системное окно установки можно только этим событием и
// только один раз, причем строго в обработчике клика пользователя.
//
// В iOS Safari программной установки нет вовсе — там только «Поделиться → На
// экран „Домой"». Поэтому кнопка установки показывается только когда событие
// пришло (checkIfAppInstallAvailable), а на iOS ее заменяет инструкция.

import {isRunningAsInstalledPwa} from '../fullscreen';
import {AD_GOALS, trackAdGoal} from '../../analytics/adTracking';
import {sendPageEvent} from '../../analytics/gameAnalytics';
import {getDeviceKind} from '../device/getDeviceKind';

let deferredPrompt = null;
let listeners = [];
// Установил ли игрок приложение с нашей кнопки (системное окно показали мы и
// он согласился) или мимо нее — из меню браузера. Без этого разделения
// конверсия промо-модалки считалась бы вместе с установками из меню
let isPromptAccepted = false;
// Цель установки — одна на установку: страховка от повторной подписки, если
// initAppInstallPrompt позовут дважды
let isInstalledGoalSent = false;

const notify = () => listeners.forEach((listener) => listener());

const handleBeforeInstallPrompt = (event) => {
  // Без preventDefault Chrome покажет свою плашку вместо нашей кнопки
  event.preventDefault();
  deferredPrompt = event;
  notify();
};

const handleAppInstalled = () => {
  // Цель рекламных кабинетов: именно этот момент означает установку (в
  // Яндекс Метрике и VK Рекламе цель заводится с этим же именем)
  if (!isInstalledGoalSent) {
    isInstalledGoalSent = true;
    const source = isPromptAccepted ? 'prompt' : 'browser_menu';
    const device = getDeviceKind();
    trackAdGoal(AD_GOALS.APP_INSTALLED, {params: {source, device}});
    sendPageEvent('MainMenu', 'app_installed', source, device);
  }
  // Использованное событие второй раз не сработает
  deferredPrompt = null;
  notify();
};

/**
 * Начинает слушать событие установки. Зовется один раз на старте приложения:
 * beforeinstallprompt приходит вскоре после загрузки страницы, и подписка из
 * компонента главного меню его бы уже не застала
 */
export const initAppInstallPrompt = () => {
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.addEventListener('appinstalled', handleAppInstalled);
};

/**
 * Можно ли предложить установку прямо сейчас: событие перехвачено и
 * приложение еще не запущено как установленное (детект установленного —
 * общий, из utils/fullscreen: там же он нужен полноэкранному режиму)
 *
 * @returns {boolean}
 */
export const checkIfAppInstallAvailable = () => Boolean(deferredPrompt) && !isRunningAsInstalledPwa();

/**
 * Подписка на появление/исчезновение возможности установки — событие приходит
 * асинхронно, уже после первого рендера меню
 *
 * @param {Function} listener
 * @returns {Function} отписка
 */
export const subscribeToAppInstallAvailability = (listener) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((item) => item !== listener);
  };
};

/**
 * Показывает системное окно установки. Вызывать только из обработчика клика:
 * браузер требует пользовательского жеста
 *
 * @returns {Promise<'accepted'|'dismissed'|'unavailable'>}
 */
export const promptAppInstall = async () => {
  if (!deferredPrompt) {
    return 'unavailable';
  }

  const prompt = deferredPrompt;
  // Событие одноразовое: второй prompt() по нему браузер отклонит
  deferredPrompt = null;
  notify();

  try {
    await prompt.prompt();
    const choice = await prompt.userChoice;
    const isAccepted = choice?.outcome === 'accepted';
    // Само событие установки придет отдельно (appinstalled) — здесь только
    // запоминаем, что окно показали мы
    isPromptAccepted = isPromptAccepted || isAccepted;
    return isAccepted ? 'accepted' : 'dismissed';
  } catch (error) {
    console.error('[PWA] Install prompt error', error);
    return 'unavailable';
  }
};

// Только для тестов: сбросить перехваченное событие между кейсами
export const resetAppInstallPromptForTests = () => {
  deferredPrompt = null;
  listeners = [];
  isPromptAccepted = false;
  isInstalledGoalSent = false;
};

// Только для тестов: подставить событие установки
export const setAppInstallPromptForTests = (event) => {
  deferredPrompt = event;
  notify();
};
